import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin, RefreshCw, Star } from "lucide-react";

/**
 * AdminGoogleReviewsCard
 * Two admin utilities that make Google review import work at scale:
 *   1. Backfill google_place_id — runs Places Text Search for every
 *      business missing a place_id, storing the top match. This is what
 *      unlocks Google review pulls for the ~500 CSV-imported businesses.
 *   2. Refresh all cached Google reviews on demand (the scheduler also
 *      does this weekly on Sunday 06:00 UTC).
 *
 * Includes a live count so the admin can see progress across runs.
 */
export default function AdminGoogleReviewsCard() {
  const [linkedCount, setLinkedCount] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [limit, setLimit] = useState(50);
  const [backfilling, setBackfilling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/stats`);
      setTotalCount(r.data.businesses || r.data.total_businesses || null);
    } catch {
      /* ignore */
    }
    // approximate linked count via the community stats endpoint if present
    try {
      const r2 = await axios.get(`${API}/community/stats`);
      setLinkedCount(r2.data?.linked_google || null);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const backfill = async () => {
    if (!window.confirm(`Run Places Text Search on up to ${limit} businesses without a Google link? Each lookup costs ~$0.017.`)) return;
    setBackfilling(true);
    try {
      const r = await axios.post(
        `${API}/admin/external-reviews/backfill-place-ids`,
        { limit, only_verified: onlyVerified }
      );
      toast.success(
        `Linked ${r.data.linked}/${r.data.attempted} businesses to Google. ${r.data.skipped_count} skipped.`
      );
      loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const refreshAll = async () => {
    if (!window.confirm("Force refresh Google reviews for every linked business? This may take a minute.")) return;
    setRefreshing(true);
    try {
      const r = await axios.post(
        `${API}/admin/external-reviews/refresh`,
        { all: true }
      );
      toast.success(`Refreshed ${r.data.refreshed}/${r.data.attempted} businesses.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4" data-testid="admin-google-reviews-card">
      <div>
        <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
          <Star className="w-5 h-5 text-tbc-gold" /> Google Reviews Import
        </h2>
        <p className="text-sm text-gray-500 font-body">
          Pulls up to 5 Google reviews per business into the profile, and
          powers the TBC Trust Score. Auto-refreshes every Sunday.
        </p>
      </div>

      {/* Backfill controls */}
      <div className="bg-tbc-navy/5 border border-tbc-navy/10 rounded-lg p-4 space-y-3">
        <div>
          <h3 className="font-heading text-sm text-tbc-navy flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Step 1 — Link businesses to Google
          </h3>
          <p className="text-xs text-gray-500 font-body">
            Runs Places Text Search on <em>&ldquo;{`{name} {city} {state}`}&rdquo;</em> to find each business&apos;s Google listing. Skips ones already linked. About $0.017 per lookup.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label htmlFor="backfill-limit" className="text-xs">Max lookups this run</Label>
            <input
              id="backfill-limit"
              type="number"
              min={1}
              max={200}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(200, parseInt(e.target.value) || 50)))}
              className="w-24 mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm"
              data-testid="google-backfill-limit"
            />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch
              checked={onlyVerified}
              onCheckedChange={setOnlyVerified}
              data-testid="google-backfill-only-verified"
            />
            <Label className="text-sm">Verified only</Label>
          </div>
          <Button
            onClick={backfill}
            disabled={backfilling}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="google-backfill-run"
          >
            {backfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
            Run backfill
          </Button>
        </div>
      </div>

      {/* Refresh */}
      <div className="bg-tbc-gold/5 border border-tbc-gold/30 rounded-lg p-4 space-y-3">
        <div>
          <h3 className="font-heading text-sm text-tbc-navy flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Step 2 — Refresh reviews on demand
          </h3>
          <p className="text-xs text-gray-500 font-body">
            Auto-runs every Sunday 06:00 UTC. Only use this after linking new businesses or fixing bad data.
          </p>
        </div>
        <Button
          onClick={refreshAll}
          disabled={refreshing}
          variant="outline"
          className="border-tbc-gold text-tbc-navy hover:bg-tbc-gold/20"
          data-testid="google-refresh-all"
        >
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Force refresh all
        </Button>
      </div>

      <p className="text-xs text-gray-400 font-body">
        Total businesses: {totalCount ?? "…"}
      </p>
    </div>
  );
}
